import { ReactElement, useEffect, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Chip,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
} from '@rossum/ui/material';

export type PromptSuggestion = {
  id: string;
  prompt: string;
};

export type PromptSuggestionCategory<TIconKey extends string = string> = {
  name: string;
  icon: TIconKey;
  label: string;
  prompts: readonly PromptSuggestion[];
};

type PromptSuggestionsChipCarouselProps<TIconKey extends string> = {
  categories: PromptSuggestionCategory<TIconKey>[];
  iconMap: Record<TIconKey, ReactElement>;
  onPromptSelect: (prompt: string) => void;
  disabled?: boolean;
  className?: string;
};

export const PromptSuggestionsChipCarousel = <TIconKey extends string>({
  categories,
  iconMap,
  onPromptSelect,
  disabled,
  className,
}: PromptSuggestionsChipCarouselProps<TIconKey>) => {
  const [popoverState, setPopoverState] = useState<{
    anchorEl: HTMLElement | null;
    category: PromptSuggestionCategory<TIconKey> | null;
  }>({
    anchorEl: null,
    category: null,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Center the carousel on first render
  useEffect(() => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      scrollContainerRef.current.scrollLeft = (scrollWidth - clientWidth) / 2;
    }
  }, []);

  const handleChipClick = (
    event: React.MouseEvent<HTMLElement>,
    category: PromptSuggestionCategory<TIconKey>
  ) => {
    setPopoverState({
      anchorEl: event.currentTarget,
      category,
    });
  };

  const handleClose = () => {
    setPopoverState({
      anchorEl: null,
      category: null,
    });
  };

  const handlePromptClick = (prompt: string) => {
    onPromptSelect(prompt);
    handleClose();
  };

  return (
    <Box className={className} sx={{ position: 'relative', width: '100%' }}>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 120,
          background: theme =>
            `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 120,
          background: theme =>
            `linear-gradient(270deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            margin: 'auto',
            minWidth: 'fit-content',
            paddingLeft: '10%',
            paddingRight: '10%',
          }}
        >
          {categories.map(category => {
            const icon = iconMap[category.icon];
            return (
              <Chip
                key={category.name}
                disabled={disabled}
                icon={icon}
                label={category.label}
                onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                  handleChipClick(e, category)
                }
                data-cy="prompt-suggestions-chip-carousel-chip"
              />
            );
          })}
        </Box>
      </Box>

      <Menu
        open={Boolean(popoverState.anchorEl)}
        anchorEl={popoverState.anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 400,
              maxWidth: 600,
              maxHeight: 400,
            },
          },
        }}
      >
        {popoverState.category && (
          <MenuList sx={{ py: 0 }}>
            {popoverState.category.prompts.map(prompt => (
              <MenuItem
                key={prompt.id}
                onClick={() => handlePromptClick(prompt.prompt)}
                data-cy={`prompt-suggestions-chip-carousel-menu-item-${prompt.id}`}
                sx={{
                  py: 1,
                  px: 2,
                }}
              >
                <ListItemText
                  primary={prompt.prompt}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { lineHeight: 1.5, textWrap: 'wrap' },
                  }}
                />
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Menu>
    </Box>
  );
};
